import api from './api';

/**
 * Exposure service for handling privacy exposure detection and analysis
 */
export const exposureService = {
  /**
   * Analyze content for privacy exposures
   * Uses the privacy_analysis module from backend
   * @param {Array} contents - Array of content items to analyze
   * @returns {Promise}
   */
  analyzePrivacy: async (contents) => {
    try {
      const response = await api.post('/privacy/analyze/', { contents });
      return response.data;
    } catch (error) {
      console.error('Analyze privacy error:', error);
      throw error;
    }
  },

  /**
   * Get all privacy findings for current user
   * @param {string} username - Username (required)
   * @param {object} filters - Filter options (risk_level, platform, exposure_type)
   * @returns {Promise}
   */
  getExposures: async (username, filters = {}) => {
    try {
      const params = { ...filters };
      if (username) params.username = username;
      if (!params.username && !params.user_id) {
        console.error('Username or user_id is required for getExposures');
        return [];
      }
      const response = await api.get('/privacy/findings/', { params });
      return response.data?.data || response.data || [];
    } catch (error) {
      console.error('Get exposures error:', error);
      // Return empty array if no findings
      return [];
    }
  },

  /**
   * Get single exposure details
   * @param {string} exposureId - Exposure ID
   * @returns {Promise}
   */
  getExposureDetails: async (exposureId) => {
    try {
      const response = await api.get(`/privacy/finding/${exposureId}/`);
      return response.data;
    } catch (error) {
      console.error('Get exposure details error:', error);
      throw error;
    }
  },

  /**
   * Get risk score for current user
   * @param {string} username - Username (required)
   * @param {object} filters - Same filters supported by `getExposures` (e.g. platform)
   * @returns {Promise}
   */
  getRiskScore: async (username, filters = {}) => {
    try {
      const params = { ...filters };
      if (username) params.username = username;
      if (!params.username && !params.user_id) {
        console.error('Username or user_id is required for getRiskScore');
        return {
          risk_score: 0,
          risk_level: 'Low',
          high: 0,
          medium: 0,
          low: 0,
          resolved: 0,
          total_unresolved: 0,
        };
      }
      const response = await api.get('/privacy/risk_score/', { params });
      return (
        response.data?.data ||
        response.data || {
          risk_score: 0,
          risk_level: 'Low',
          high: 0,
          medium: 0,
          low: 0,
          resolved: 0,
          total_unresolved: 0,
        }
      );
    } catch (error) {
      console.error('Get risk score error:', error);
      return {
        risk_score: 0,
        risk_level: 'Low',
        high: 0,
        medium: 0,
        low: 0,
        resolved: 0,
        total_unresolved: 0,
      };
    }
  },

  /**
   * Get exposure statistics
   * @param {string} username - Username (required)
   * @param {object} filters - Same filters supported by `getExposures` (e.g. platform)
   * @returns {Promise}
   */
  getExposureStats: async (username, filters = {}) => {
    try {
      if (!username && !filters.user_id) {
        console.error('Username or user_id is required for getExposureStats');
        return {
          total: 0,
          byType: {},
          byRiskLevel: { high: 0, medium: 0, low: 0 },
          resolved: 0,
          unresolved: 0
        };
      }
      const exposures = await exposureService.getExposures(username, filters);
      
      const stats = {
        total: exposures.length,
        byType: {},
        byRiskLevel: {
          high: 0,
          medium: 0,
          low: 0
        },
        resolved: 0,
        unresolved: 0
      };
      
      exposures.forEach(exposure => {
        // Count by type
        stats.byType[exposure.exposure_type] = (stats.byType[exposure.exposure_type] || 0) + 1;
        
        // Count by risk level (case-insensitive)
        const riskLevel = exposure.risk_level?.toLowerCase();
        if (riskLevel === 'high') {
          stats.byRiskLevel.high++;
        } else if (riskLevel === 'medium') {
          stats.byRiskLevel.medium++;
        } else if (riskLevel === 'low') {
          stats.byRiskLevel.low++;
        }
        
        // Count resolved/unresolved
        if (exposure.is_resolved) {
          stats.resolved++;
        } else {
          stats.unresolved++;
        }
      });
      
      return stats;
    } catch (error) {
      console.error('Get exposure stats error:', error);
      return {
        total: 0,
        byType: {},
        byRiskLevel: { high: 0, medium: 0, low: 0 },
        resolved: 0,
        unresolved: 0
      };
    }
  },

  /**
   * Get cross-platform risk analysis (Phase 2)
   * @returns {Promise}
   */
  getCrossPlatformRisk: async () => {
    try {
      const response = await api.get('/detection/cross-platform-risk/');
      return response.data;
    } catch (error) {
      console.error('Get cross-platform risk error:', error);
      throw error;
    }
  },

  /**
   * Get AI chatbot guidance for exposures
   * @param {object} data - Request data (action, exposure_data)
   * @returns {Promise}
   */
  getChatbotGuidance: async (data) => {
    try {
      const response = await api.post('/privacy/guide/', data);
      return response.data;
    } catch (error) {
      console.error('Get chatbot guidance error:', error);
      throw error;
    }
  },

  /**
   * Mark exposure as resolved
   * @param {string} exposureId - Exposure ID
   * @param {string} username - Username (required)
   * @param {string} platform - Platform (default: reddit)
   * @returns {Promise}
   */
  resolveExposure: async (exposureId, username, platform = 'reddit', mergedResolutionIds = null) => {
    try {
      const body = { username, platform };
      if (Array.isArray(mergedResolutionIds) && mergedResolutionIds.length > 0) {
        body.merged_resolution_ids = mergedResolutionIds;
      }
      const response = await api.post(`/privacy/resolve/${exposureId}/`, body);
      return response.data;
    } catch (error) {
      console.error('Resolve exposure error:', error);
      throw error;
    }
  },
};

export default exposureService;
